import { collection, getDocs, query, Timestamp, where } from 'firebase/firestore';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useMicrospecDynamicUIContext } from '../../GlobalProvider';
import { deleteCookie, getStandardMicrospecCookie, setStandardMicrospecCookie } from '../../Helpers/LocalPersistenceUtils';

interface Module {
  id: string;
  type: string;
  end_date: Timestamp;
  [key: string]: any;
}

interface Organization {
  id: string;
  name: string;
  membershipId?: string;
  roles?: string[];
  memberStatus?: string;
  enabledModules?: Module[];
  [key: string]: any;
}

interface Branch {
  id: string;
  name: string;
  membershipId?: string;
  roles?: string[];
  memberStatus?: string;
  [key: string]: any;
}

interface OrganizationContextValue {
  // Data
  organizations: Organization[];
  branches: Branch[];
  selectedOrganization: Organization | null;
  organizationLocalization: any;
  selectedBranch: Branch | null;
  branchLocalization: any;
  usedLocalization: any;

  // State
  loading: boolean;
  error: string | null;
  isOrganizationContextAware: boolean;
  hasMemberships: boolean;

  // Actions
  setSelectedOrganization: (organization: Organization | null) => void;
  setSelectedBranch: (branch: Branch | null) => void;
  refresh: () => void;

  // Computed values
  organizationId: string | null;
  branchId: string | null;
  organizationName: string | null;
  branchName: string | null;
  organizationRoles: string[];
  branchRoles: string[];
  enabledModules: Module[];

  // Helper functions
  isModuleEnabled: (moduleType: string) => boolean;
  getModuleData: (moduleType: string) => Module | null;
}

interface OrganizationContextProviderProps {
  children: ReactNode;
  organizationContextAware?: boolean;
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

/**
 * Organization Context Provider
 * Manages organization and branch context for dashboards that require multi-tenancy awareness
 * 
 * Features:
 * - Loads user's organization memberships
 * - Loads branch memberships within selected organization
 * - Persists context selection in localStorage
 * - Provides context switching capabilities
 * - Tracks loading and error states
 */
export const OrganizationContextProvider: React.FC<OrganizationContextProviderProps> = ({
  children,
  organizationContextAware = false
}) => {
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { user } = cloudProvider;
  const { firestore } = cloudProvider;
  const db = firestore?.instance;

  // State management
  const [organizations, setOrganization] = useState<Organization[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [organizationLocalization, setOrganizationLocalization] = useState<any>(null);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [branchLocalization, setBranchLocalization] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Load user's organization memberships
  const loadOrganizationMemberships = useCallback(async () => {
    if (!user || !db || !organizationContextAware) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Query all organizations where user is a member
      const organizationsSnapshot = await getDocs(collection(db, 'organizations'));
      const now = Timestamp.now();

      const organizationPromises = organizationsSnapshot.docs.map(async (organizationDoc) => {
        const organizationData = { id: organizationDoc.id, ...organizationDoc.data() };

        // Check if user is a member of this organization
        const membersQuery = query(
          collection(db, `organizations/${organizationDoc.id}/members`),
          where('user_id', '==', user.uid),
          where('status', '==', 'active')
        );

        const membersSnapshot = await getDocs(membersQuery);

        if (!membersSnapshot.empty) {
          const memberData = membersSnapshot.docs[0].data();

          // Query enabled modules for this organization
          let enabledModules: Module[] = [];
          try {
            const modulesQuery = query(
              collection(db, `organizations/${organizationDoc.id}/modules`),
              where('end_date', '>', now)
            );
            const modulesSnapshot = await getDocs(modulesQuery);
            enabledModules = modulesSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            } as Module));
          } catch (err) {
            console.error(`Error loading modules for organization ${organizationDoc.id}:`, err);
          }

          return {
            ...organizationData,
            membershipId: membersSnapshot.docs[0].id,
            roles: memberData.roles || [],
            memberStatus: memberData.status,
            enabledModules: enabledModules
          } as Organization;
        }
        return null;
      });

      const organizationResults = await Promise.all(organizationPromises);
      const userOrganization = organizationResults.filter((b): b is Organization => b !== null);
      setOrganization(userOrganization);

      // Auto-select organization from localStorage or first available
      if (userOrganization.length > 0) {
        const savedOrganizationId = getStandardMicrospecCookie('selectedOrganizationId');
        const organizationToSelect = userOrganization.find(b => b.id === savedOrganizationId) || userOrganization[0];
        setSelectedOrganization(organizationToSelect);
        setOrganizationLocalization(organizationToSelect.localization || null);
      } else {
        setSelectedOrganization(null);
        setOrganizationLocalization(null);
        setBranches([]);
        setSelectedBranch(null);
      }

    } catch (err: any) {
      console.error('Error loading organization memberships:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user, db, organizationContextAware]);

  // Load branches for selected organization
  const loadBranchMemberships = useCallback(async (organizationId: string) => {
    if (!user || !db || !organizationId) {
      setBranches([]);
      setBranchLocalization(null);
      setSelectedBranch(null);
      return;
    }

    try {
      // Query all branches for this organization
      const branchesSnapshot = await getDocs(collection(db, `organizations/${organizationId}/branches`));
      const branchPromises = branchesSnapshot.docs.map(async (branchDoc) => {
        const branchData = { id: branchDoc.id, ...branchDoc.data() };

        // Check if user is a member of this branch
        const membersQuery = query(
          collection(db, `organizations/${organizationId}/branches/${branchDoc.id}/members`),
          where('user_id', '==', user.uid),
          where('status', '==', 'active')
        );

        const membersSnapshot = await getDocs(membersQuery);

        if (!membersSnapshot.empty) {
          const memberData = membersSnapshot.docs[0].data();
          return {
            ...branchData,
            membershipId: membersSnapshot.docs[0].id,
            roles: memberData.roles || [],
            memberStatus: memberData.status
          } as Branch;
        }
        return null;
      });

      const branchResults = await Promise.all(branchPromises);
      const userBranches = branchResults.filter((b): b is Branch => b !== null);

      setBranches(userBranches);

      // Auto-select branch from localStorage or first available
      if (userBranches.length > 0) {
        const savedBranchId = getStandardMicrospecCookie(`selectedBranchId_${organizationId}`);
        const branchToSelect = userBranches.find(b => b.id === savedBranchId) || userBranches[0];
        setSelectedBranch(branchToSelect);
        setBranchLocalization(branchToSelect.localization || null);
      } else {
        setSelectedBranch(null);
        setBranchLocalization(null);
      }

    } catch (err: any) {
      console.error('Error loading branch memberships:', err);
      setError(err.message);
    }
  }, [user, db]);

  // Load initial data
  useEffect(() => {
    if (organizationContextAware) {
      loadOrganizationMemberships();
    }
  }, [organizationContextAware, loadOrganizationMemberships]);

  // Load branches when organization changes
  useEffect(() => {
    if (selectedOrganization) {
      loadBranchMemberships(selectedOrganization.id);
    } else {
      setBranches([]);
      setSelectedBranch(null);
      setBranchLocalization(null);
    }
  }, [selectedOrganization, loadBranchMemberships]);

  // Handle organization selection
  const handleOrganizationChange = useCallback((organization: Organization | null) => {
    setSelectedOrganization(organization);
    setOrganizationLocalization(organization ? (organization.localization || null) : null);
    setSelectedBranch(null);
    setBranchLocalization(null);

    if (organization) {
      setStandardMicrospecCookie('selectedOrganizationId', organization.id);
    } else {
      deleteCookie('selectedOrganizationId');
    }
  }, []);

  // Handle branch selection
  const handleBranchChange = useCallback((branch: Branch | null) => {
    setSelectedBranch(branch);
    setBranchLocalization(branch ? (branch.localization || null) : null);

    if (branch && selectedOrganization) {
      setStandardMicrospecCookie(`selectedBranchId_${selectedOrganization.id}`, branch.id);
    } else if (selectedOrganization) {
      deleteCookie(`selectedBranchId_${selectedOrganization.id}`);
    }
  }, [selectedOrganization]);

  // Refresh context data
  const refresh = useCallback(() => {
    if (organizationContextAware) {
      loadOrganizationMemberships();
    }
  }, [organizationContextAware, loadOrganizationMemberships]);

  // Context value
  const value = useMemo<OrganizationContextValue>(() => ({
    // Data
    organizations,
    branches,
    selectedOrganization,
    organizationLocalization,
    selectedBranch,
    branchLocalization,
    usedLocalization: branchLocalization || organizationLocalization || {
      preferred: 'en-US',
      supported: ['en-US']
    },

    // State
    loading,
    error,
    isOrganizationContextAware: organizationContextAware,
    hasMemberships: organizations.length > 0,

    // Actions
    setSelectedOrganization: handleOrganizationChange,
    setSelectedBranch: handleBranchChange,
    refresh,

    // Computed values
    organizationId: selectedOrganization?.id || null,
    branchId: selectedBranch?.id || null,
    organizationName: selectedOrganization?.name || null,
    branchName: selectedBranch?.name || null,
    organizationRoles: selectedOrganization?.roles || [],
    branchRoles: selectedBranch?.roles || [],
    enabledModules: selectedOrganization?.enabledModules || [],

    // Helper functions
    isModuleEnabled: (moduleType: string): boolean => {
      const modules = selectedOrganization?.enabledModules || [];
      return modules.some(module => module.type === moduleType);
    },
    getModuleData: (moduleType: string): Module | null => {
      const modules = selectedOrganization?.enabledModules || [];
      return modules.find(module => module.type === moduleType) || null;
    },
  }), [
    organizations,
    branches,
    selectedOrganization,
    organizationLocalization,
    selectedBranch,
    branchLocalization,
    loading,
    error,
    organizationContextAware,
    handleOrganizationChange,
    handleBranchChange,
    refresh
  ]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

/**
 * Hook to use Organization Context
 * @returns Organization context value
 */
export const useOrganizationContext = (): OrganizationContextValue => {
  const context = useContext(OrganizationContext);

  if (context === null) {
    throw new Error('useOrganizationContext must be used within OrganizationContextProvider');
  }

  return context;
};

export default OrganizationContext;
export type { Branch, Module, Organization, OrganizationContextValue };

